import { ImageProtection } from '@app/components/ImageProtection';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import ChevronLeftIcon from '@redbubble/design-system/src/Icons/ChevronLeft';
import ChevronRightIcon from '@redbubble/design-system/src/Icons/ChevronRight';
import Image from '@redbubble/design-system/src/Image';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import {
  defineMessages,
  MessageDescriptor,
  useIntl,
} from '@redbubble/next/intl';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { A11y, Zoom } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/zoom';
import 'swiper/css/a11y';
import {
  getImageRatioClass,
  getPreviewFallback,
  getPreviewSources,
  imageRatio,
  Picture,
} from '@app/components/Picture';
import { AppProps } from '@redbubble/next/types';
import { usePageProps } from '@redbubble/next-provider';
import styles from './ProductPreviewsImage.module.scss';
import { Preview } from './types';

export interface ProductPreviewImageProps {
  selectedPreview: Preview;
  previews: Preview[];
  artistName: string;
  productCategoryName: string;
  workTitle: string;
  onChange: (preview: Preview) => void;
}

const messages = defineMessages({
  productAltText: {
    defaultMessage:
      '{productCategoryName}, {workTitle} designed and sold by {artistName}',
  },
  artworkAltText: {
    defaultMessage:
      'Artwork view, {workTitle} designed and sold by {artistName}',
  },
  containerMessage: {
    defaultMessage:
      'Product images, {workTitle} {productCategoryName}, designed and sold by {artistName}',
  },
  firstSlideMessage: {
    defaultMessage: 'This is the first image of {slidesLength}',
  },
  lastSlideMessage: {
    defaultMessage: 'This is the last image of {slidesLength}',
  },
  nextSlideMessage: {
    defaultMessage: 'Show next image, {index} of {slidesLength}',
  },
  prevSlideMessage: {
    defaultMessage: 'Show previous image, {index} of {slidesLength}',
  },
  slideLabelMessage: {
    defaultMessage: 'Image {index} of {slidesLength}',
  },
  paginationLabelMessage: {
    defaultMessage: 'Show image {index} of {slidesLength}',
  },
});

interface ProductPictureProps {
  preview: Preview;
  selectedPreview: Preview;
  index: number;
  getAltText: (preview: Preview) => string;
}

export const ProductPicture = ({
  preview,
  selectedPreview,
  getAltText,
  index,
}: ProductPictureProps) => {
  const sources = getPreviewSources(preview.formats);
  const img = getPreviewFallback(preview.formats);

  return img ? (
    <Picture
      ratioPaddingClass={getImageRatioClass(imageRatio.RATIO_PORTRAIT)}
      roundedCorners
    >
      {sources.map(({ url, mime }) => (
        <Picture.Source srcSet={url} type={mime} key={url} />
      ))}
      <Picture.Img
        src={img.url}
        alt={getAltText(preview)}
        ariaHidden={preview.url !== selectedPreview.url}
        loading={index === 0 ? 'eager' : 'lazy'}
      />
    </Picture>
  ) : (
    <Image
      fluid
      alt={getAltText(preview)}
      src={preview.url}
      roundedCorners
      aria-hidden={preview.url !== selectedPreview.url}
    />
  );
};

export const ProductPreviewImage = ({
  selectedPreview,
  previews,
  artistName,
  workTitle,
  productCategoryName,
  onChange,
}: ProductPreviewImageProps) => {
  const intl = useIntl();
  const { deviceType } = usePageProps<AppProps>();

  const pointerDownTimeStamp = useRef<number>(0);
  const triggerZoomClick = useRef<boolean>(true);

  const swiperRef = useRef<SwiperRef | null>(null);

  const getFormattedMessage = useCallback(
    (message: MessageDescriptor, additionalValues?: Record<string, string>) => {
      return intl.formatMessage(message, {
        workTitle,
        artistName,
        productCategoryName,
        slidesLength: `${previews.length}`,
        ...additionalValues,
      });
    },
    [intl, workTitle, artistName, productCategoryName, previews]
  );

  const getAltText = (preview: Preview) => {
    if (preview.previewTypeId === 'artwork') {
      return getFormattedMessage(messages.artworkAltText);
    }

    return getFormattedMessage(messages.productAltText);
  };

  const previewIndex = useMemo(() => {
    const currentIndex = previews.findIndex(
      (preview) => preview.url === selectedPreview?.url
    );

    return Math.max(currentIndex, 0);
  }, [previews, selectedPreview]);

  const previousPreview = useMemo<Preview | undefined>(() => {
    return previews[previewIndex - 1];
  }, [previews, previewIndex]);
  const previousPreviewTitle = useMemo<string>(() => {
    return getFormattedMessage(
      previousPreview ? messages.prevSlideMessage : messages.firstSlideMessage,
      {
        index: `${previewIndex - 1}`,
      }
    );
  }, [previousPreview, getFormattedMessage, previewIndex]);

  const nextPreview = useMemo<Preview | undefined>(() => {
    return previews[previewIndex + 1];
  }, [previews, previewIndex]);
  const nextPreviewTitle = useMemo<string>(() => {
    return getFormattedMessage(
      nextPreview ? messages.nextSlideMessage : messages.lastSlideMessage,
      {
        index: `${previewIndex + 1}`,
      }
    );
  }, [nextPreview, getFormattedMessage, previewIndex]);

  const desktopSingleClickZoom = useCallback(
    (event: globalThis.MouseEvent | TouchEvent | PointerEvent) => {
      if (deviceType === 'desktop' && triggerZoomClick.current)
        swiperRef.current?.swiper.zoom.toggle(event);
    },
    [deviceType]
  );

  const onPointerDown = useCallback(() => {
    if (deviceType === 'desktop') {
      pointerDownTimeStamp.current = Date.now();
      triggerZoomClick.current = true;
    }
  }, [deviceType]);

  const onPointerUp = useCallback(() => {
    if (
      deviceType === 'desktop' &&
      swiperRef.current?.swiper.zoom.scale !== 1 &&
      Date.now() - pointerDownTimeStamp.current > 200
    )
      triggerZoomClick.current = false;
  }, [deviceType]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(previewIndex);
    }
  }, [previewIndex, swiperRef]);

  return (
    <Box className={styles.container} data-testid="product-preview-image">
      <Box className={styles.swiperContainer}>
        <ImageProtection>
          <Swiper
            ref={swiperRef}
            slidesPerView={1}
            onSlideChangeTransitionStart={(swiper) => {
              onChange(previews[swiper.activeIndex]);
            }}
            className={[styles.swiper, styles.zoomEnabled].join(' ')}
            modules={[A11y, Zoom]}
            zoom={{
              containerClass: 'custom-zoom-container',
              maxRatio: deviceType === 'desktop' ? 1.5 : 2,
            }}
            a11y={{
              enabled: true,
              containerMessage: getFormattedMessage(messages.containerMessage),
              firstSlideMessage: getFormattedMessage(
                messages.firstSlideMessage
              ),
              lastSlideMessage: getFormattedMessage(messages.lastSlideMessage),
              nextSlideMessage: getFormattedMessage(messages.nextSlideMessage, {
                index: `${previewIndex + 1}`,
              }),
              prevSlideMessage: getFormattedMessage(messages.prevSlideMessage, {
                index: `${previewIndex - 1}`,
              }),
              slideLabelMessage: getFormattedMessage(
                messages.slideLabelMessage,
                {
                  index: '{{index}}',
                }
              ),
            }}
          >
            {previews.map((preview, i) => (
              <SwiperSlide key={preview.url} zoom>
                <Box
                  onPointerDown={onPointerDown}
                  onPointerUp={onPointerUp}
                  onClick={(e) => desktopSingleClickZoom(e)}
                  className={`custom-zoom-container ${styles.fullWidth}`}
                >
                  <ProductPicture
                    preview={preview}
                    selectedPreview={selectedPreview}
                    getAltText={getAltText}
                    index={i}
                  />

                  <Box
                    className={styles.loadingPlaceholder}
                    data-testid={`product-previews-${preview.previewTypeId}-image-placeholder`}
                  >
                    <Skeleton roundedCorners ratio={constants.RATIO_PORTRAIT} />
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </ImageProtection>

        <Box className={styles.navigation}>
          <Box className={styles.previous}>
            <Button
              size={constants.MEDIUM}
              type="button"
              inverse={previousPreview && true}
              disabled={!previousPreview}
              onClick={() => previousPreview && onChange(previousPreview)}
              circle
              strong
              iconBefore={<ChevronLeftIcon size={constants.MEDIUM} />}
              title={previousPreviewTitle}
              aria-label={previousPreviewTitle}
            />
          </Box>

          <Box className={styles.next}>
            <Button
              size={constants.MEDIUM}
              type="button"
              inverse={nextPreview && true}
              disabled={!nextPreview}
              onClick={() => nextPreview && onChange(nextPreview)}
              circle
              strong
              iconBefore={<ChevronRightIcon size={constants.MEDIUM} />}
              title={nextPreviewTitle}
              aria-label={nextPreviewTitle}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
